import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductTileConfig } from "@omni/types/components/product/productTileConfig.type";
import { ReactNode, Ref, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import ProductTileComponent from "@/components/Product/ProductTile/productTile.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import { load as loadCart } from "@/redux/modules/api/data/cart/cart.module";
import {
    getCart,
    isSuccessfullyLoaded
} from "@/redux/modules/api/data/cart/cart.selectors";
import { stringToBoolean } from "@/utils/attributes";

type ProductTileProps = {
    sku: string;
    product: Product<ProductFieldSet.ProductTile>;
    allowInstantAddToCart: string | boolean;
    extraButtons?: () => ReactNode;
    cqDataPath?: string;
    children?: ReactNode;
    lazyLoadImage?: boolean;
    productTileConfig: ProductTileConfig;
    analyticsContext?: {
        placement?: number;
        list: string;
    };
    amount?: number;
    realtimeStockPromise?: Promise<number | null>;
    onClickTrack?: () => void;
    className?: string;
    ref?: Ref<HTMLAnchorElement | HTMLDivElement>;
};

const ProductTile = ({
    ref,
    sku,
    product,
    allowInstantAddToCart,
    extraButtons,
    cqDataPath,
    children,
    lazyLoadImage,
    productTileConfig,
    amount,
    analyticsContext,
    onClickTrack,
    className,
    realtimeStockPromise
}: ProductTileProps) => {
    const dispatch = useThunkDispatch();
    const cartIsLoaded = useSelector(isSuccessfullyLoaded);
    const cart = useSelector(getCart);

    const [clientSideProduct, setClientSideProduct] = useState(product);
    const [_, loadProducts] = useBFFProductServiceApi<
        any,
        any,
        Product<ProductFieldSet.ProductTile>
    >(
        {
            url: `/products/:sku`,
            method: "GET"
        },
        {
            manual: true,
            useCache: false,
            autoCancel: false
        }
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const isClientSideRender = !!cqDataPath;
        if (isClientSideRender) {
            getProductData(sku);
            if (!cartIsLoaded) dispatch(loadCart());
        }
    }, []);

    const getProductData = (sku: string) => {
        if (sku) {
            loadProducts({
                urlParams: { sku: sku },
                params: {
                    fields: ProductFieldSet.ProductTile
                }
            }).then(({ data }) => {
                setClientSideProduct(data);
            });
        }
    };

    const handleExtraOnClick = () => {
        if (clientSideProduct.url) {
            location.assign(clientSideProduct.url);
        }
    };

    if (!clientSideProduct) {
        return null;
    }

    return (
        <ProductTileComponent
            extraButtons={extraButtons}
            className={className}
            product={product}
            allowInstantAddToCart={stringToBoolean(`${allowInstantAddToCart}`)}
            cart={cart || undefined}
            extraOnClick={handleExtraOnClick}
            lazyLoadImage={lazyLoadImage}
            productTileConfig={productTileConfig}
            ref={ref}
            amount={amount}
            analyticsContext={analyticsContext}
            onClickTracking={onClickTrack}
            realtimeStockPromise={realtimeStockPromise}
        >
            {children}
        </ProductTileComponent>
    );
};

export { ProductTile };
